import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { generateImageUrlFromImd } from '../Util';
import { listFanPolls } from './fanPollApi';

const STORAGE_KEY = 'fan_poll_manager_last_search';
const stateColor = (state) => ({ DRAFT: 'default', SCHEDULED: 'info', ACTIVE: 'success', CLOSED: 'warning' }[state] ?? 'default');

function PollThumbnail({ imageMetadata, label }) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        let active = true;
        generateImageUrlFromImd(imageMetadata).then(value => { if (active) setUrl(value); });
        return () => { active = false; };
    }, [imageMetadata]);
    return url ? <Box component="img" src={url} alt={label ?? ''} sx={{ width: 72, height: 42, objectFit: 'cover', borderRadius: 1 }} /> : null;
}

const formatDate = (value) => value ? new Date(value).toLocaleString() : '-';

export default function FanPollManager({ open, onClose, onLoad }) {
    const [search, setSearch] = useState(localStorage.getItem(STORAGE_KEY) ?? '');
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false);

    const fetchPolls = useCallback(async (term) => {
        setLoading(true);
        setError('');
        const trimmed = (term ?? '').trim();
        if (trimmed) localStorage.setItem(STORAGE_KEY, trimmed);
        else localStorage.removeItem(STORAGE_KEY);
        try {
            const result = await listFanPolls(trimmed);
            setPolls(Array.isArray(result) ? result : []);
            setLoaded(true);
        } catch (requestError) {
            setError(requestError.message ?? String(requestError));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open && search && !loaded) fetchPolls(search);
    }, [open, search, loaded, fetchPolls]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>Load fan poll</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                    <TextField size="small" label="Search fan polls" value={search} onChange={event => setSearch(event.target.value)}
                        onKeyDown={event => { if (event.key === 'Enter') fetchPolls(search); }} sx={{ width: 340 }} />
                    <Button variant="contained" onClick={() => fetchPolls(search)} disabled={loading}>Search</Button>
                    <Button variant="outlined" onClick={() => { setSearch(''); fetchPolls(''); }} disabled={loading}>Load all</Button>
                    {!loaded && !loading && <Typography color="text.secondary">Search or load all polls.</Typography>}
                </Box>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
                {error && <Typography color="error">{error}</Typography>}
                {!loading && loaded && (
                    <TableContainer sx={{ maxHeight: '65vh' }}>
                        <Table stickyHeader size="small">
                            <TableHead><TableRow>
                                <TableCell>Photo</TableCell><TableCell>Label / question</TableCell><TableCell>Type</TableCell>
                                <TableCell>State</TableCell><TableCell>Main tag</TableCell><TableCell>Tags</TableCell>
                                <TableCell>Start / end</TableCell><TableCell>Modified</TableCell><TableCell />
                            </TableRow></TableHead>
                            <TableBody>{polls.map(poll => (
                                <TableRow key={poll.id} hover>
                                    <TableCell><PollThumbnail imageMetadata={poll.imageMetadata} label={poll.label} /></TableCell>
                                    <TableCell><Typography fontWeight={700}>{poll.label}</Typography><Typography variant="caption">{poll.question}</Typography></TableCell>
                                    <TableCell>{poll.pollType}</TableCell>
                                    <TableCell><Chip size="small" label={poll.state} color={stateColor(poll.state)} /></TableCell>
                                    <TableCell>{poll.mainTag?.label ?? '-'}</TableCell>
                                    <TableCell>{(poll.tagList ?? []).map(tag => tag.label).join(', ')}</TableCell>
                                    <TableCell><Typography variant="caption">{formatDate(poll.startDate)}<br />{formatDate(poll.endDate)}</Typography></TableCell>
                                    <TableCell>{formatDate(poll.modifyDate)}</TableCell>
                                    <TableCell><Button size="small" variant="contained" onClick={() => onLoad(poll.id)}>Load</Button></TableCell>
                                </TableRow>
                            ))}</TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
}
